<template>
  <div class="pa-panel" :lang="uiLang">
    <!-- Sekmeler solda, Excel indirme sağda: kardeş rapor sekmeleriyle aynı düzen -->
    <div class="pa-toolbar">
      <div class="pa-tabs">
        <button
            v-for="tab in tabs" :key="tab.key"
            class="pa-tab" :class="{ active: activeTab === tab.key }"
            @click="setTab(tab.key)"
        >{{ $t(tab.label) }}</button>
      </div>

      <TableDownload
          v-if="exportConfig"
          :data="exportConfig.data"
          :fields="exportConfig.fields"
          :headers="exportConfig.headers"
          :computed-fields="exportConfig.computedFields"
          :filename="exportConfig.filename"
          :label="$t('programAnalytics.download_excel')"
      />
    </div>

    <!-- Filtreler -->
    <div class="pa-filters">
      <template v-if="activeTab !== 'trivia'">
          <DropdownSelect
              :label="$t('programAnalytics.program')"
              :modelValue="filters.programId"
              :options="programOptionsFiltered"
              :searchable="true"
              :maxTextLength="30"
              @search="(q) => programSearch = q"
              @update:modelValue="onProgramChange"
          />
          <DropdownSelect
              :label="$t('programAnalytics.module')"
              :modelValue="filters.moduleId"
              :options="moduleOptions"
              :maxTextLength="30"
              @update:modelValue="(v) => filters.moduleId = v"
          />
      </template>
      <template v-else>
          <DropdownSelect
              :label="$t('programAnalytics.trivia_exam')"
              :modelValue="filters.triviaExamId"
              :options="examOptionsFiltered"
              :searchable="true"
              :maxTextLength="30"
              @search="(q) => examSearch = q"
              @update:modelValue="(v) => filters.triviaExamId = v"
          />
      </template>

        <DropdownSelect
            :label="$t('programAnalytics.department')"
            :modelValue="filters.departmentId"
            :options="departmentOptionsFiltered"
            :searchable="true"
            :maxTextLength="30"
            @search="(q) => departmentSearch = q"
            @update:modelValue="(v) => filters.departmentId = v"
        />

      <div class="pa-date-group">
        <div class="pa-chip">
          <span class="pa-chip-label">{{ $t('programAnalytics.start_date') }}:</span>
          <input class="pa-chip-field" type="date" v-model="filters.startDate" />
        </div>
        <div class="pa-chip">
          <span class="pa-chip-label">{{ $t('programAnalytics.end_date') }}:</span>
          <input class="pa-chip-field" type="date" v-model="filters.endDate" />
        </div>
      </div>

      <DropdownSelect
          v-if="activeTab === 'duration'"
          :label="$t('programAnalytics.max_gap')"
          :modelValue="filters.maxSeconds"
          :options="maxSecondsOptions"
          :maxTextLength="12"
          @update:modelValue="(v) => filters.maxSeconds = v"
      />

      <button class="pa-action-btn" :disabled="loading || !canLoad" @click="load">
        {{ $t('programAnalytics.apply') }}
      </button>
    </div>

    <div class="pa-hint" v-if="activeTab !== 'trivia' && !filters.programId">
      {{ $t('programAnalytics.select_program_hint') }}
    </div>

    <div class="pa-loading" v-if="loading">
      <VueSpinner size="18" color="#8c8c8c" />
      <span>{{ $t('loading') }}</span>
    </div>

    <!-- ================= HUNİ ================= -->
    <div v-if="!loading && activeTab === 'funnel' && funnel">
      <div class="pa-cards">
        <div class="pa-card"><span class="pa-card-value">{{ funnel.summary.assignedUsers }}</span><span class="pa-card-label">{{ $t('programAnalytics.assigned_users') }}</span></div>
        <div class="pa-card"><span class="pa-card-value">{{ funnel.summary.startedUsers }}</span><span class="pa-card-label">{{ $t('programAnalytics.started_users') }}</span></div>
        <div class="pa-card"><span class="pa-card-value">{{ funnel.summary.completedUsers }}</span><span class="pa-card-label">{{ $t('programAnalytics.completed_users') }}</span></div>
      </div>

      <div class="pa-empty" v-if="funnel.modules.length === 0">{{ $t('no_data_found') }}</div>

      <div class="pa-module" v-for="module in funnel.modules" :key="module.moduleId">
        <div class="pa-module-header">
          <h4>{{ module.order }}. {{ module.name }}</h4>
          <span class="pa-muted">
            {{ $t('programAnalytics.reached') }}: <b>{{ module.reachedUsers }}</b> ·
            {{ $t('programAnalytics.module_completed') }}: <b>{{ module.completedUsers }}</b>
          </span>
        </div>

        <apexchart
            v-if="module.steps.length > 0"
            type="bar"
            :height="Math.max(160, module.steps.length * 28 + 60)"
            :options="funnelChartOptions(module)"
            :series="[{ name: $t('programAnalytics.reached'), data: module.steps.map(s => stepShare(module, s.reached)) }]"
        />

        <div class="table-container">
          <table class="content-table">
            <thead class="header-table">
            <tr class="trhead">
              <th>#</th>
              <th>{{ $t('programAnalytics.step') }}</th>
              <th>{{ $t('programAnalytics.type') }}</th>
              <th>{{ $t('programAnalytics.reached') }}</th>
              <th>{{ $t('programAnalytics.passed') }}</th>
              <th>{{ $t('programAnalytics.stuck') }}</th>
              <th>{{ $t('programAnalytics.stuck_pct') }}</th>
              <th>{{ $t('programAnalytics.drop_off_pct') }}</th>
            </tr>
            </thead>
            <tbody class="tbody-container">
            <tr class="tr-class" v-for="(step, i) in module.steps" :key="step.stepId">
              <td class="td-style">{{ i + 1 }}</td>
              <td class="td-style pa-cell-wrap">{{ step.name || '-' }}</td>
              <td class="td-style"><span class="pa-pill">{{ typeLabel(step.questionType || step.nodeType) }}</span></td>
              <td class="td-style">{{ step.reached }}</td>
              <td class="td-style">{{ step.passed }}</td>
              <!-- Adıma kimse gelmediyse "kalan 0" yazmak yanıltıcı olur, tire gösteriyoruz -->
              <td class="td-style" :class="{ 'pa-warn': step.stuck > 0 }">{{ step.reached > 0 ? step.stuck : '—' }}</td>
              <td class="td-style">{{ step.reached > 0 ? formatPct(step.stuckPct) : '—' }}</td>
              <td class="td-style" :class="{ 'pa-warn': step.dropOffPct >= 20 }">{{ formatPct(step.dropOffPct) }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ================= ADIM SÜRELERİ ================= -->
    <div v-if="!loading && activeTab === 'duration' && duration">
      <div class="pa-note">{{ $t('programAnalytics.duration_note') }}</div>

      <div class="pa-module" v-if="duration.topSteps.length > 0">
        <div class="pa-module-header"><h4>{{ $t('programAnalytics.top_steps') }}</h4></div>
        <div class="table-container">
          <table class="content-table">
            <thead class="header-table">
            <tr class="trhead">
              <th>{{ $t('programAnalytics.module') }}</th>
              <th>{{ $t('programAnalytics.step') }}</th>
              <th>{{ $t('programAnalytics.type') }}</th>
              <th>{{ $t('programAnalytics.median') }}</th>
              <th>{{ $t('programAnalytics.p90') }}</th>
              <th>{{ $t('programAnalytics.samples') }}</th>
            </tr>
            </thead>
            <tbody class="tbody-container">
            <tr class="tr-class" v-for="step in duration.topSteps" :key="step.stepId">
              <td class="td-style pa-cell-wrap">{{ step.moduleName }}</td>
              <td class="td-style pa-cell-wrap">{{ step.name || '-' }}</td>
              <td class="td-style"><span class="pa-pill">{{ typeLabel(step.questionType) }}</span></td>
              <td class="td-style"><b>{{ formatDuration(step.medianSeconds) }}</b></td>
              <td class="td-style">{{ formatDuration(step.p90Seconds) }}</td>
              <td class="td-style">{{ step.samples }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="pa-empty" v-if="duration.modules.length === 0">{{ $t('no_data_found') }}</div>

      <div class="pa-module" v-for="module in duration.modules" :key="module.moduleId">
        <div class="pa-module-header">
          <h4>{{ module.order }}. {{ module.name }}</h4>
          <span class="pa-muted">
            {{ $t('programAnalytics.approx_module_time') }}: <b>{{ formatDuration(module.medianSeconds) }}</b>
            <template v-if="module.declaredMinutes"> · {{ $t('programAnalytics.declared_time') }}: {{ module.declaredMinutes }} {{ $t('programAnalytics.minute_short') }}</template>
            · {{ $t('programAnalytics.users') }}: {{ module.users }}
          </span>
        </div>
        <div class="table-container">
          <table class="content-table">
            <thead class="header-table">
            <tr class="trhead">
              <th>#</th>
              <th>{{ $t('programAnalytics.step') }}</th>
              <th>{{ $t('programAnalytics.type') }}</th>
              <th>{{ $t('programAnalytics.median') }}</th>
              <th>{{ $t('programAnalytics.average') }}</th>
              <th>{{ $t('programAnalytics.p90') }}</th>
              <th>{{ $t('programAnalytics.samples') }}</th>
              <th>{{ $t('programAnalytics.outliers') }}</th>
            </tr>
            </thead>
            <tbody class="tbody-container">
            <tr class="tr-class" v-for="(step, i) in module.steps" :key="step.stepId">
              <td class="td-style">{{ i + 1 }}</td>
              <td class="td-style pa-cell-wrap">{{ step.name || '-' }}</td>
              <td class="td-style"><span class="pa-pill">{{ typeLabel(step.questionType) }}</span></td>
              <td class="td-style"><b>{{ formatDuration(step.medianSeconds) }}</b></td>
              <td class="td-style">{{ formatDuration(step.avgSeconds) }}</td>
              <td class="td-style">{{ formatDuration(step.p90Seconds) }}</td>
              <td class="td-style">{{ step.samples }}</td>
              <td class="td-style">{{ step.outliers }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ================= YANLIŞ CEVAPLAR (PROGRAM) ================= -->
    <div v-if="!loading && activeTab === 'wrong' && wrong">
      <div class="pa-cards">
        <div class="pa-card"><span class="pa-card-value">{{ wrong.summary.markedQuestionCount }}</span><span class="pa-card-label">{{ $t('programAnalytics.marked_questions') }}</span></div>
        <div class="pa-card"><span class="pa-card-value">{{ wrong.summary.unmarkedQuestionCount }}</span><span class="pa-card-label">{{ $t('programAnalytics.unmarked_questions') }}</span></div>
        <div class="pa-card"><span class="pa-card-value">{{ wrong.summary.answeredCount }}</span><span class="pa-card-label">{{ $t('programAnalytics.answers') }}</span></div>
        <div class="pa-card"><span class="pa-card-value" :class="{ 'pa-warn': wrong.summary.wrongCount > 0 }">{{ wrong.summary.wrongCount }}</span><span class="pa-card-label">{{ $t('programAnalytics.wrong_answers') }}</span></div>
      </div>
      <div class="pa-note" v-if="wrong.summary.unmarkedQuestionCount > 0">{{ $t('programAnalytics.unmarked_note') }}</div>

      <div class="pa-subtabs">
        <button class="pa-tab small" :class="{ active: wrongView === 'questions' }" @click="wrongView = 'questions'">{{ $t('programAnalytics.by_question') }}</button>
        <button class="pa-tab small" :class="{ active: wrongView === 'users' }" @click="wrongView = 'users'">{{ $t('programAnalytics.by_user') }}</button>
      </div>

      <div class="table-container" v-if="wrongView === 'questions'">
        <table class="content-table">
          <thead class="header-table">
          <tr class="trhead">
            <th>{{ $t('programAnalytics.question') }}</th>
            <th>{{ $t('programAnalytics.module') }}</th>
            <th>{{ $t('programAnalytics.type') }}</th>
            <th>{{ $t('programAnalytics.correct_answer') }}</th>
            <th>{{ $t('programAnalytics.answered') }}</th>
            <th>{{ $t('programAnalytics.wrong') }}</th>
            <th>{{ $t('programAnalytics.wrong_pct') }}</th>
            <th>{{ $t('programAnalytics.retried') }}</th>
            <th class="pa-col-action"></th>
          </tr>
          </thead>
          <tbody class="tbody-container">
          <template v-for="q in wrong.questions" :key="q.questionId">
            <tr class="tr-class">
              <td class="td-style pa-cell-wrap">{{ q.name }}</td>
              <td class="td-style pa-cell-wrap">{{ q.moduleName }}</td>
              <td class="td-style"><span class="pa-pill">{{ typeLabel(q.type) }}</span></td>
              <td class="td-style pa-cell-wrap">{{ q.correctOptions.join(', ') }}</td>
              <td class="td-style">{{ q.answered }}</td>
              <td class="td-style" :class="{ 'pa-warn': q.wrong > 0 }">{{ q.wrong }}</td>
              <td class="td-style">{{ formatPct(q.wrongPct) }}</td>
              <td class="td-style">{{ q.retried }} <span class="pa-muted" v-if="q.retried">({{ q.retryCorrect }} ✓)</span></td>
              <td class="td-style pa-col-action">
                <button class="pa-link" v-if="q.wrongUsers.length" @click="toggle('q', q.questionId)">
                  {{ expanded['q' + q.questionId] ? $t('programAnalytics.hide') : $t('programAnalytics.show_users') }}
                </button>
              </td>
            </tr>
            <tr v-if="expanded['q' + q.questionId]" class="pa-expand">
              <td colspan="9">
                <div class="pa-expand-row" v-for="u in q.wrongUsers" :key="u.userId">
                  <b>{{ u.name || u.email }}</b>
                  <span class="pa-muted">{{ u.email }}</span>
                  <span>{{ $t('programAnalytics.given_answer') }}: {{ u.given.join(', ') || '-' }}</span>
                  <span v-if="u.retried">{{ $t('programAnalytics.retried') }}: {{ u.retryCorrect ? '✓' : '✗' }}</span>
                  <span class="pa-muted">{{ formatDateTime(u.answeredAt) }}</span>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="wrong.questions.length === 0"><td colspan="9" class="table-loading"><span class="text">{{ $t('no_data_found') }}</span></td></tr>
          </tbody>
        </table>
      </div>

      <div class="table-container" v-else>
        <table class="content-table">
          <thead class="header-table">
          <tr class="trhead">
            <th>{{ $t('programAnalytics.user') }}</th>
            <th>{{ $t('email') }}</th>
            <th>{{ $t('programAnalytics.answered') }}</th>
            <th>{{ $t('programAnalytics.wrong') }}</th>
            <th>{{ $t('programAnalytics.wrong_pct') }}</th>
            <th>{{ $t('programAnalytics.retried') }}</th>
            <th class="pa-col-action"></th>
          </tr>
          </thead>
          <tbody class="tbody-container">
          <template v-for="u in wrong.users" :key="u.userId">
            <tr class="tr-class">
              <td class="td-style">{{ u.name || '-' }}</td>
              <td class="td-style">{{ u.email }}</td>
              <td class="td-style">{{ u.answered }}</td>
              <td class="td-style" :class="{ 'pa-warn': u.wrong > 0 }">{{ u.wrong }}</td>
              <td class="td-style">{{ formatPct(u.wrongPct) }}</td>
              <td class="td-style">{{ u.retried }}</td>
              <td class="td-style pa-col-action">
                <button class="pa-link" v-if="u.wrongQuestions.length" @click="toggle('u', u.userId)">
                  {{ expanded['u' + u.userId] ? $t('programAnalytics.hide') : $t('programAnalytics.show_questions') }}
                </button>
              </td>
            </tr>
            <tr v-if="expanded['u' + u.userId]" class="pa-expand">
              <td colspan="7">
                <div class="pa-expand-row" v-for="q in u.wrongQuestions" :key="q.questionId">
                  <b>{{ q.name }}</b>
                  <span class="pa-muted">{{ q.moduleName }}</span>
                  <span>{{ $t('programAnalytics.given_answer') }}: {{ q.given.join(', ') || '-' }}</span>
                  <span v-if="q.retried">{{ $t('programAnalytics.retried') }}: {{ q.retryCorrect ? '✓' : '✗' }}</span>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="wrong.users.length === 0"><td colspan="7" class="table-loading"><span class="text">{{ $t('no_data_found') }}</span></td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ================= TRIVIA ================= -->
    <div v-if="!loading && activeTab === 'trivia' && trivia">
      <div class="pa-cards">
        <div class="pa-card"><span class="pa-card-value">{{ trivia.summary.questionCount }}</span><span class="pa-card-label">{{ $t('programAnalytics.questions') }}</span></div>
        <div class="pa-card"><span class="pa-card-value">{{ trivia.summary.userCount }}</span><span class="pa-card-label">{{ $t('programAnalytics.users') }}</span></div>
        <div class="pa-card"><span class="pa-card-value">{{ trivia.summary.attempts }}</span><span class="pa-card-label">{{ $t('programAnalytics.attempts') }}</span></div>
        <div class="pa-card"><span class="pa-card-value" :class="{ 'pa-warn': trivia.summary.wrong > 0 }">{{ trivia.summary.wrong }}</span><span class="pa-card-label">{{ $t('programAnalytics.wrong_answers') }}</span></div>
      </div>

      <div class="pa-subtabs">
        <button class="pa-tab small" :class="{ active: triviaView === 'questions' }" @click="triviaView = 'questions'">{{ $t('programAnalytics.by_question') }}</button>
        <button class="pa-tab small" :class="{ active: triviaView === 'users' }" @click="triviaView = 'users'">{{ $t('programAnalytics.by_user') }}</button>
      </div>

      <div class="table-container" v-if="triviaView === 'questions'">
        <table class="content-table">
          <thead class="header-table">
          <tr class="trhead">
            <th>{{ $t('programAnalytics.trivia_exam') }}</th>
            <th>{{ $t('programAnalytics.question') }}</th>
            <th>{{ $t('programAnalytics.correct_answer') }}</th>
            <th>{{ $t('programAnalytics.attempts') }}</th>
            <th>{{ $t('programAnalytics.wrong') }}</th>
            <th>{{ $t('programAnalytics.wrong_pct') }}</th>
            <th>{{ $t('programAnalytics.users_wrong') }}</th>
            <th>{{ $t('programAnalytics.never_correct') }}</th>
            <!-- Hiç kimse doğruya ulaşmadıysa sütun tamamen boş kalıyordu, gizliyoruz -->
            <th v-if="triviaShowAvgAttempts">{{ $t('programAnalytics.avg_attempts_to_correct') }}</th>
            <th>{{ $t('programAnalytics.top_wrong_answers') }}</th>
            <th class="pa-col-action"></th>
          </tr>
          </thead>
          <tbody class="tbody-container">
          <template v-for="q in trivia.questions" :key="q.questionId">
            <tr class="tr-class">
              <td class="td-style pa-cell-narrow">{{ q.examName }}</td>
              <td class="td-style pa-cell-wrap">{{ q.questionText }}</td>
              <td class="td-style pa-cell-narrow">{{ q.correctAnswer }}</td>
              <td class="td-style">{{ q.attempts }}</td>
              <td class="td-style" :class="{ 'pa-warn': q.wrong > 0 }">{{ q.wrong }}</td>
              <td class="td-style">{{ formatPct(q.wrongPct) }}</td>
              <td class="td-style">{{ q.usersWrong }} / {{ q.users }}</td>
              <td class="td-style">{{ q.usersNeverCorrect }}</td>
              <td class="td-style" v-if="triviaShowAvgAttempts">{{ q.avgAttemptsToCorrect ?? '-' }}</td>
              <td class="td-style pa-cell-narrow">{{ q.topWrongAnswers.map(a => `${a.answer} (${a.count})`).join(', ') || '-' }}</td>
              <td class="td-style pa-col-action">
                <button class="pa-link" v-if="q.wrongUsers.length" @click="toggle('tq', q.questionId)">
                  {{ expanded['tq' + q.questionId] ? $t('programAnalytics.hide') : $t('programAnalytics.show_users') }}
                </button>
              </td>
            </tr>
            <tr v-if="expanded['tq' + q.questionId]" class="pa-expand">
              <td :colspan="triviaColspan">
                <div class="pa-expand-row" v-for="u in q.wrongUsers" :key="u.userId">
                  <b>{{ u.name || u.email }}</b>
                  <span class="pa-muted">{{ u.email }}</span>
                  <span>{{ $t('programAnalytics.wrong_answers') }}: {{ u.wrongAnswers.join(', ') }}</span>
                  <span>{{ $t('programAnalytics.attempts') }}: {{ u.attempts }}</span>
                  <span>{{ u.solved ? $t('programAnalytics.solved_on_attempt', { n: u.attemptsToCorrect }) : $t('programAnalytics.not_solved') }}</span>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="trivia.questions.length === 0"><td :colspan="triviaColspan" class="table-loading"><span class="text">{{ $t('no_data_found') }}</span></td></tr>
          </tbody>
        </table>
      </div>

      <div class="table-container" v-else>
        <table class="content-table">
          <thead class="header-table">
          <tr class="trhead">
            <th>{{ $t('programAnalytics.user') }}</th>
            <th>{{ $t('email') }}</th>
            <th>{{ $t('programAnalytics.questions') }}</th>
            <th>{{ $t('programAnalytics.attempts') }}</th>
            <th>{{ $t('programAnalytics.wrong') }}</th>
            <th>{{ $t('programAnalytics.wrong_pct') }}</th>
            <th>{{ $t('programAnalytics.questions_wrong') }}</th>
            <th>{{ $t('programAnalytics.never_correct') }}</th>
            <th class="pa-col-action"></th>
          </tr>
          </thead>
          <tbody class="tbody-container">
          <template v-for="u in trivia.users" :key="u.userId">
            <tr class="tr-class">
              <td class="td-style">{{ u.name || '-' }}</td>
              <td class="td-style">{{ u.email }}</td>
              <td class="td-style">{{ u.questions }}</td>
              <td class="td-style">{{ u.attempts }}</td>
              <td class="td-style" :class="{ 'pa-warn': u.wrong > 0 }">{{ u.wrong }}</td>
              <td class="td-style">{{ formatPct(u.wrongPct) }}</td>
              <td class="td-style">{{ u.questionsWrong }}</td>
              <td class="td-style">{{ u.questionsNeverCorrect }}</td>
              <td class="td-style pa-col-action">
                <button class="pa-link" v-if="u.wrongQuestions.length" @click="toggle('tu', u.userId)">
                  {{ expanded['tu' + u.userId] ? $t('programAnalytics.hide') : $t('programAnalytics.show_questions') }}
                </button>
              </td>
            </tr>
            <tr v-if="expanded['tu' + u.userId]" class="pa-expand">
              <td colspan="9">
                <div class="pa-expand-row" v-for="q in u.wrongQuestions" :key="q.questionId">
                  <b>{{ q.questionText }}</b>
                  <span class="pa-muted">{{ q.examName }}</span>
                  <span>{{ $t('programAnalytics.correct_answer') }}: {{ q.correctAnswer }}</span>
                  <span>{{ $t('programAnalytics.wrong_answers') }}: {{ q.wrongAnswers.join(', ') }}</span>
                  <span>{{ q.solved ? $t('programAnalytics.solved_on_attempt', { n: q.attemptsToCorrect }) : $t('programAnalytics.not_solved') }}</span>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="trivia.users.length === 0"><td colspan="9" class="table-loading"><span class="text">{{ $t('no_data_found') }}</span></td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import DropdownSelect from "@/components/DropdownSelect.vue";
import TableDownload from "@/components/TableDownload.vue";
import { VueSpinner } from "vue3-spinners";
import analyticsService from "@/service/analyticsService";
import programService from "@/service/programService";
import depService from "@/service/depService";
import { handleErrorResponse, formatDateDMYHI } from "@/utils/utils";

// Ham soru/düğüm tipleri kullanıcıya gösterilmeden önce çevrilir.
const TYPE_LABELS = {
  tr: {
    single: 'Tek seçim', multiple: 'Çoklu seçim', yesNo: 'Evet/Hayır', dragAndDrop: 'Sürükle bırak',
    welcomeText: 'Karşılama', success: 'Başarı ekranı', failed: 'Başarısız ekranı', summary: 'Özet',
    text: 'Metin', video: 'Video', audio: 'Ses', grading: 'Derecelendirme', number: 'Sayı',
    rating: 'Puanlama', fillTheBlank: 'Boşluk doldurma', swipe: 'Kaydırma', matchType: 'Eşleştirme',
    education: 'Eğitim', gender: 'Cinsiyet', selfChat: 'İç konuşma', scenarioText: 'Senaryo metni',
    agentRedirect: 'Asistana yönlendirme',
    part: 'Adım', question: 'Soru', 'number-question': 'Sayısal soru', 'one-way-question': 'Tek yönlü soru',
  },
  en: {
    single: 'Single choice', multiple: 'Multiple choice', yesNo: 'Yes/No', dragAndDrop: 'Drag and drop',
    welcomeText: 'Welcome', success: 'Success screen', failed: 'Failure screen', summary: 'Summary',
    text: 'Text', video: 'Video', audio: 'Audio', grading: 'Grading', number: 'Number',
    rating: 'Rating', fillTheBlank: 'Fill in the blank', swipe: 'Swipe', matchType: 'Matching',
    education: 'Education', gender: 'Gender', selfChat: 'Self chat', scenarioText: 'Scenario text',
    agentRedirect: 'Agent redirect',
    part: 'Step', question: 'Question', 'number-question': 'Numeric question', 'one-way-question': 'One-way question',
  },
};

export default {
  name: "ProgramAnalyticsPanel",
  components: { DropdownSelect, TableDownload, VueSpinner },
  data() {
    return {
      tabs: [
        { key: 'funnel',   label: 'programAnalytics.tab_funnel' },
        { key: 'duration', label: 'programAnalytics.tab_duration' },
        { key: 'wrong',    label: 'programAnalytics.tab_wrong' },
        { key: 'trivia',   label: 'programAnalytics.tab_trivia' },
      ],
      activeTab: 'funnel',
      loading: false,
      filters: {
        programId: '',
        moduleId: 'all',
        departmentId: 'all',
        triviaExamId: 'all',
        startDate: '',
        endDate: '',
        maxSeconds: 43200,
      },
      programs: [],
      departments: [],
      exams: [],
      modules: [],
      funnel: null,
      duration: null,
      wrong: null,
      trivia: null,
      wrongView: 'questions',
      triviaView: 'questions',
      expanded: {},
      // DropdownSelect yalnızca 'search' olayı yayar, filtrelemeyi üst bileşen yapar
      programSearch: '',
      departmentSearch: '',
      examSearch: '',
    };
  },
  computed: {
    // text-transform: uppercase Türkçe'de İ/I ayrımı için lang bilgisine ihtiyaç duyar
    uiLang() {
      return localStorage.getItem('userLanguage') || 'tr';
    },
    canLoad() {
      return this.activeTab === 'trivia' || !!this.filters.programId;
    },
    programOptions() {
      return [{ value: '', label: this.$t('programAnalytics.select_program') }]
          .concat(this.programs.map(p => ({
            value: p.programId,
            label: this.localized(p.name) || this.$t('programAnalytics.unnamed_program'),
          })));
    },
    programOptionsFiltered() {
      return this.searchFilter(this.programOptions, this.programSearch);
    },
    departmentOptionsFiltered() {
      return this.searchFilter(this.departmentOptions, this.departmentSearch);
    },
    examOptionsFiltered() {
      return this.searchFilter(this.examOptions, this.examSearch);
    },
    // Gerçek adım süreleri saniyeler mertebesinde, seçenekler bu yüzden 1 dakikadan başlıyor
    maxSecondsOptions() {
      const min = this.$t('programAnalytics.minute_short');
      const hour = this.$t('programAnalytics.hour_short');
      return [
        { value: 60, label: `1 ${min}` },
        { value: 300, label: `5 ${min}` },
        { value: 900, label: `15 ${min}` },
        { value: 1800, label: `30 ${min}` },
        { value: 3600, label: `1 ${hour}` },
        { value: 14400, label: `4 ${hour}` },
        { value: 43200, label: `12 ${hour}` },
        { value: 86400, label: `24 ${hour}` },
      ];
    },
    triviaShowAvgAttempts() {
      return !!this.trivia && this.trivia.questions.some(q => q.avgAttemptsToCorrect !== null && q.avgAttemptsToCorrect !== undefined);
    },
    triviaColspan() {
      return this.triviaShowAvgAttempts ? 11 : 10;
    },
    moduleOptions() {
      return [{ value: 'all', label: this.$t('programAnalytics.all_modules') }]
          .concat(this.modules.map(m => ({ value: m.moduleId, label: `${m.order}. ${m.name}` })));
    },
    departmentOptions() {
      return [{ value: 'all', label: this.$t('programAnalytics.all_departments') }]
          .concat(this.departments.map(d => ({ value: d.departmentId, label: this.localized(d.name) })));
    },
    examOptions() {
      return [{ value: 'all', label: this.$t('programAnalytics.all_exams') }]
          .concat(this.exams.map(e => ({ value: e.triviaExamId, label: e.name })));
    },
    commonParams() {
      return {
        programId: this.filters.programId,
        moduleId: this.filters.moduleId,
        departmentId: this.filters.departmentId,
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
      };
    },
    exportConfig() {
      const t = (k) => this.$t('programAnalytics.' + k);
      if (this.activeTab === 'funnel' && this.funnel) {
        const rows = [];
        this.funnel.modules.forEach(m => m.steps.forEach((s, i) => rows.push({ module: m.name, index: i + 1, ...s })));
        return {
          data: rows,
          fields: ['module', 'index', 'name', 'questionType', 'reached', 'passed', 'stuck', 'stuckPct', 'dropOffPct'],
          headers: { module: t('module'), index: '#', name: t('step'), questionType: t('type'), reached: t('reached'), passed: t('passed'), stuck: t('stuck'), stuckPct: t('stuck_pct'), dropOffPct: t('drop_off_pct') },
          computedFields: {},
          filename: t('tab_funnel'),
        };
      }
      if (this.activeTab === 'duration' && this.duration) {
        const rows = [];
        this.duration.modules.forEach(m => m.steps.forEach((s, i) => rows.push({ module: m.name, index: i + 1, ...s })));
        return {
          data: rows,
          fields: ['module', 'index', 'name', 'questionType', 'medianSeconds', 'avgSeconds', 'p90Seconds', 'samples', 'users', 'outliers'],
          headers: { module: t('module'), index: '#', name: t('step'), questionType: t('type'), medianSeconds: t('median'), avgSeconds: t('average'), p90Seconds: t('p90'), samples: t('samples'), users: t('users'), outliers: t('outliers') },
          computedFields: {
            medianSeconds: (r) => this.formatDuration(r.medianSeconds),
            avgSeconds: (r) => this.formatDuration(r.avgSeconds),
            p90Seconds: (r) => this.formatDuration(r.p90Seconds),
          },
          filename: t('tab_duration'),
        };
      }
      if (this.activeTab === 'wrong' && this.wrong) {
        if (this.wrongView === 'questions') {
          return {
            data: this.wrong.questions,
            fields: ['name', 'moduleName', 'type', 'correctOptions', 'answered', 'wrong', 'wrongPct', 'retried', 'retryCorrect'],
            headers: { name: t('question'), moduleName: t('module'), type: t('type'), correctOptions: t('correct_answer'), answered: t('answered'), wrong: t('wrong'), wrongPct: t('wrong_pct'), retried: t('retried'), retryCorrect: t('retry_correct') },
            computedFields: { correctOptions: (r) => r.correctOptions.join(', ') },
            filename: t('tab_wrong'),
          };
        }
        const rows = [];
        this.wrong.users.forEach(u => u.wrongQuestions.forEach(q => rows.push({ user: u.name, email: u.email, ...q })));
        return {
          data: rows,
          fields: ['user', 'email', 'name', 'moduleName', 'given', 'retried', 'retryCorrect'],
          headers: { user: t('user'), email: this.$t('email'), name: t('question'), moduleName: t('module'), given: t('given_answer'), retried: t('retried'), retryCorrect: t('retry_correct') },
          computedFields: { given: (r) => r.given.join(', ') },
          filename: t('tab_wrong') + '_' + t('by_user'),
        };
      }
      if (this.activeTab === 'trivia' && this.trivia) {
        if (this.triviaView === 'questions') {
          return {
            data: this.trivia.questions,
            fields: ['examName', 'questionText', 'correctAnswer', 'attempts', 'wrong', 'wrongPct', 'users', 'usersWrong', 'usersNeverCorrect', 'avgAttemptsToCorrect', 'topWrongAnswers'],
            headers: { examName: t('trivia_exam'), questionText: t('question'), correctAnswer: t('correct_answer'), attempts: t('attempts'), wrong: t('wrong'), wrongPct: t('wrong_pct'), users: t('users'), usersWrong: t('users_wrong'), usersNeverCorrect: t('never_correct'), avgAttemptsToCorrect: t('avg_attempts_to_correct'), topWrongAnswers: t('top_wrong_answers') },
            computedFields: { topWrongAnswers: (r) => r.topWrongAnswers.map(a => `${a.answer} (${a.count})`).join(', ') },
            filename: t('tab_trivia'),
          };
        }
        const rows = [];
        this.trivia.users.forEach(u => u.wrongQuestions.forEach(q => rows.push({ user: u.name, email: u.email, ...q })));
        return {
          data: rows,
          fields: ['user', 'email', 'examName', 'questionText', 'correctAnswer', 'wrongAnswers', 'attempts', 'solved', 'attemptsToCorrect'],
          headers: { user: t('user'), email: this.$t('email'), examName: t('trivia_exam'), questionText: t('question'), correctAnswer: t('correct_answer'), wrongAnswers: t('wrong_answers'), attempts: t('attempts'), solved: t('solved'), attemptsToCorrect: t('attempts_to_correct') },
          computedFields: { wrongAnswers: (r) => r.wrongAnswers.join(', '), solved: (r) => r.solved ? '✓' : '✗' },
          filename: t('tab_trivia') + '_' + t('by_user'),
        };
      }
      return null;
    },
  },
  methods: {
    // Türkçe küçültme "I" harfini noktasız "ı" yapar ve İngilizce veriyle eşleşmez.
    // Arama karşılaştırmasında i/ı/İ/I sınıfını tek karaktere indirgiyoruz.
    searchNormalize(text) {
      return (text || '').replace(/[İIıi]/g, 'i').toLowerCase();
    },
    searchFilter(options, query) {
      const q = this.searchNormalize(query).trim();
      if (!q) return options;
      // Program adları uzun; baştan değil, içinde arıyoruz
      return options.filter(o => this.searchNormalize(o.label).includes(q));
    },
    typeLabel(type) {
      if (!type) return '-';
      const map = TYPE_LABELS[this.uiLang] || TYPE_LABELS.tr;
      return map[type] || type;
    },
    formatPct(value) {
      if (value === null || value === undefined) return '-';
      const n = Math.round(Number(value) * 10) / 10;
      if (Number.isNaN(n)) return '-';
      const text = Number.isInteger(n) ? String(n) : n.toFixed(1);
      // Türkçe'de yüzde işareti sayıdan önce gelir: %20
      return this.uiLang === 'tr' ? '%' + text.replace('.', ',') : text + '%';
    },
    localized(value) {
      if (!value) return '';
      if (typeof value === 'string') return value;
      const lang = localStorage.getItem('userLanguage') || 'tr';
      return value[lang] || value.tr || value.en || Object.values(value)[0] || '';
    },
    setTab(key) {
      this.activeTab = key;
      this.expanded = {};
      if (this.canLoad && !this[this.dataKey(key)]) {
        this.load();
      }
    },
    dataKey(tab) {
      return { funnel: 'funnel', duration: 'duration', wrong: 'wrong', trivia: 'trivia' }[tab];
    },
    onProgramChange(value) {
      this.filters.programId = value;
      this.filters.moduleId = 'all';
      this.modules = [];
      this.funnel = null;
      this.duration = null;
      this.wrong = null;
      if (value) {
        this.load();
      }
    },
    toggle(prefix, id) {
      this.expanded[prefix + id] = !this.expanded[prefix + id];
    },
    async load() {
      if (!this.canLoad) return;
      const scrollY = window.scrollY;
      this.loading = true;
      this.expanded = {};
      try {
        if (this.activeTab === 'funnel') {
          const res = await analyticsService.getProgramFunnel(this.commonParams);
          this.funnel = res.data.data;
          if (this.filters.moduleId === 'all') {
            this.modules = this.funnel.modules.map(m => ({ moduleId: m.moduleId, name: m.name, order: m.order }));
          }
        } else if (this.activeTab === 'duration') {
          const res = await analyticsService.getProgramStepDuration({ ...this.commonParams, maxSeconds: this.filters.maxSeconds });
          this.duration = res.data.data;
        } else if (this.activeTab === 'wrong') {
          const res = await analyticsService.getProgramWrongAnswers(this.commonParams);
          this.wrong = res.data.data;
        } else if (this.activeTab === 'trivia') {
          const res = await analyticsService.getTriviaWrongAnswers({
            triviaExamId: this.filters.triviaExamId,
            departmentId: this.filters.departmentId,
            startDate: this.filters.startDate,
            endDate: this.filters.endDate,
          });
          this.trivia = res.data.data;
        }
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      } finally {
        this.loading = false;
        // içerik yeniden çizilirken kaydırma konumunu koru
        this.$nextTick(() => {
          if (scrollY > 0) window.scrollTo({ top: scrollY });
        });
      }
    },
    async loadLookups() {
      try {
        const [programs, departments, exams] = await Promise.allSettled([
          programService.getProgramList(),
          depService.getDepList(),
          analyticsService.getTriviaExams(),
        ]);
        if (programs.status === 'fulfilled') this.programs = programs.value.data.data || [];
        if (departments.status === 'fulfilled') this.departments = departments.value.data.data || [];
        if (exams.status === 'fulfilled') this.exams = exams.value.data.data || [];
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      }
    },
    // Adıma gelen kullanıcının, modüle giren kullanıcıya oranı
    stepShare(module, reached) {
      const base = module.reachedUsers || 0;
      if (!base) return 0;
      return Math.round((reached * 100 / base) * 10) / 10;
    },
    funnelChartOptions(module) {
      const steps = module.steps;
      return {
        chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: false } },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
        // 0 değerinde çubuk çizilmiyor, etiketi yine de göstermek için offset veriyoruz
        dataLabels: {
          enabled: true,
          offsetX: 8,
          style: { colors: ['#344054'] },
          formatter: (v, opts) => {
            const step = steps[opts.dataPointIndex];
            return step ? `${this.formatPct(v)} (${step.reached})` : this.formatPct(v);
          },
        },
        colors: ['#1C3CD5'],
        xaxis: {
          categories: steps.map((s, i) => `${i + 1}. ${(s.name || s.nodeType || '').toString().slice(0, 40)}`),
          // Kişi sayısı az olduğunda eksen 0.1, 0.2 gibi kesirli değerler üretiyordu
          min: 0,
          max: 100,
          tickAmount: 5,
          labels: { formatter: (v) => this.formatPct(v) },
        },
        tooltip: {
          y: {
            formatter: (v, opts) => {
              const step = steps[opts.dataPointIndex];
              const users = this.$t('programAnalytics.users').toLowerCase();
              return step ? `${step.reached} ${users} (${this.formatPct(v)})` : this.formatPct(v);
            },
          },
        },
      };
    },
    formatDuration(seconds) {
      if (seconds === null || seconds === undefined) return '-';
      const s = Math.round(seconds);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      const parts = [];
      if (h) parts.push(`${h} ${this.$t('programAnalytics.hour_short')}`);
      if (m) parts.push(`${m} ${this.$t('programAnalytics.minute_short')}`);
      if (!h && (sec || parts.length === 0)) parts.push(`${sec} ${this.$t('programAnalytics.second_short')}`);
      return parts.join(' ');
    },
    formatDateTime(value) {
      // Sayfanın geneliyle aynı biçim: gg.aa.yyyy ss:dd (utils.js'teki paylaşılan yardımcı)
      return value ? formatDateDMYHI(value) : '';
    },
  },
  mounted() {
    this.loadLookups();
  },
};
</script>

<style scoped>
/* Ölçüler kardeş rapor sekmelerinden alındı:
   segment kontrol .view-tab-container (40px, 8px radius, #F3F4F6),
   koyu aksiyon düğmesi .btn-report (#111827, 40px, 8px radius),
   tablo hücresi 12px/16px, kart 12px radius. */

/* --- üst şerit: sekmeler solda, indirme sağda --- */
.pa-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }

/* --- segment kontroller (ana sekmeler ve alt sekmeler aynı görünüm) --- */
.pa-tabs, .pa-subtabs {
  display: flex; align-items: center; gap: 2px;
  background: #F3F4F6; border-radius: 8px; padding: 4px; height: 40px; flex-shrink: 0;
}
.pa-subtabs { margin: 12px 0; width: fit-content; }
.pa-tab {
  padding: 0 16px; height: 32px; border: none; outline: none; background: transparent; border-radius: 6px;
  font-family: 'euclid_medium', sans-serif; font-size: 11px; color: #6B7280; cursor: pointer;
  display: flex; align-items: center; justify-content: center; white-space: nowrap; transition: all .2s ease;
}
.pa-tab:hover { color: #374151; }
.pa-tab.active { background: #fff; color: #111827; box-shadow: 0 1px 2px rgba(16, 24, 40, .06); }

/* --- filtreler: DropdownSelect ile aynı çip görünümü --- */
.pa-filters { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 14px; }
.pa-date-group { display: inline-flex; gap: 10px; flex-wrap: nowrap; }
.pa-chip { display: inline-flex; gap: 5px; }
.pa-chip-label {
  font-size: 12px; color: #333; padding: 0 8px; background-color: #f1f2f6;
  border-top-left-radius: 4px; border-bottom-left-radius: 4px;
  display: flex; align-items: center; font-family: 'euclid_medium', sans-serif; height: 23px;
}
.pa-chip-field {
  height: 23px; padding: 0 8px; border: none; outline: none; background-color: #f1f2f6;
  border-top-right-radius: 4px; border-bottom-right-radius: 4px;
  font-size: 12px; color: #4361ee; font-family: 'euclid circular a', sans-serif; cursor: pointer;
}
.pa-chip-field:hover { background-color: #e8e9ef; }

/* --- aksiyon düğmesi: kardeş sekmelerdeki koyu düğme --- */
.pa-action-btn {
  height: 40px; padding: 0 16px; border: none; border-radius: 8px; background: #111827; color: #fff;
  font-family: 'euclid_medium', sans-serif; font-size: 11px; cursor: pointer; white-space: nowrap; transition: background .2s ease;
}
.pa-action-btn:hover:not(:disabled) { background: #1F2937; }
.pa-action-btn:disabled { background: #D0D5DD; cursor: not-allowed; }

/* --- bilgi / durum --- */
.pa-hint, .pa-note {
  background: #F9FAFB; border: 1px solid #EAECF0; color: #475467;
  padding: 8px 12px; border-radius: 8px; margin-bottom: 12px; font-size: 12px;
}
.pa-loading { display: flex; align-items: center; gap: 8px; color: #667085; font-size: 12px; padding: 16px 0; }
.pa-empty { color: #667085; font-size: 12px; padding: 16px 0; }

/* --- özet kartları --- */
.pa-cards { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 14px; }
.pa-card {
  background: #fff; border: 1px solid #EAECF0; border-radius: 12px;
  padding: 12px 16px; min-width: 130px; display: flex; flex-direction: column; gap: 2px;
}
.pa-card-value { font-size: 18px; font-family: 'euclid_medium', sans-serif; color: #101828; line-height: 1.2; }
.pa-card-label { font-size: 11px; color: #667085; }

/* --- modül kutusu --- */
.pa-module { background: #fff; border: 1px solid #EAECF0; border-radius: 12px; padding: 16px; margin-bottom: 14px; }
.pa-module-header { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; flex-wrap: wrap; margin-bottom: 8px; }
.pa-module-header h4 { margin: 0; font-size: 13px; font-family: 'euclid_semi_bold', sans-serif; color: #101828; }
.pa-muted { color: #667085; font-size: 11px; }
.pa-warn { color: #D92D20; font-family: 'euclid_medium', sans-serif; }
.pa-pill { background: #F2F4F7; color: #475467; border-radius: 999px; padding: 2px 8px; font-size: 11px; white-space: nowrap; }
.pa-link { background: none; border: none; color: #1C3CD5; cursor: pointer; font-size: 12px; padding: 0; font-family: 'euclid_medium', sans-serif; white-space: nowrap; }

/* --- tablolar --- */
/* Yalnızca yatay kaydırma; dikey tekerleği tarayıcı kendi zincirlemesiyle sayfaya taşır.
   Buraya JS ile müdahale etmeyin: fare/tarayıcı deltaY birimleri (satır/piksel/sayfa)
   değiştiği için kaydırma bazı ortamlarda çok yavaşlıyor. */
.table-container { overflow-x: auto; overflow-y: hidden; border: 1px solid #EAECF0; border-radius: 12px; background: #fff; }
.pa-module .table-container { border: none; border-radius: 0; }
/* main.css'teki global "table { overflow: hidden; border; radius }" kuralı hem
   sticky sütunu iptal ediyor hem de konteynerle çift çerçeve oluşturuyordu. */
.content-table { width: 100%; border-collapse: collapse; overflow: visible; border: none; border-radius: 0; }
.header-table { background: #F9FAFB; }
.trhead th {
  padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 500; color: #475467;
  border-bottom: 1px solid #EAECF0; font-family: 'euclid_semi_bold', sans-serif;
  white-space: nowrap; user-select: none;
}
.td-style { padding: 12px 16px; border-bottom: 1px solid #F2F4F7; vertical-align: middle; text-align: left; font-size: 12px; color: #344054; }
.tr-class:hover > .td-style { background: #FCFCFD; }
.table-loading { padding: 16px; text-align: center; }
.table-loading .text { font-size: 12px; color: #667085; }

/* uzun metinler tek satırda tabloyu şişiriyordu */
.pa-cell-wrap { white-space: normal; max-width: 320px; min-width: 180px; }
.pa-cell-narrow { white-space: normal; max-width: 180px; }

/* aksiyon sütunu her zaman görünsün: yatay kaydırmada sağa sabitlenir */
.pa-col-action { position: sticky; right: 0; background: #fff; box-shadow: -8px 0 8px -8px rgba(16, 24, 40, .18); }
.header-table .pa-col-action { background: #F9FAFB; }
.tr-class:hover > .pa-col-action { background: #FCFCFD; }

/* --- açılır detay satırı --- */
.pa-expand td { background: #F9FAFB; padding: 6px 16px; }
.pa-expand-row { display: flex; gap: 12px; flex-wrap: wrap; font-size: 12px; color: #344054; padding: 4px 0; border-bottom: 1px solid #EAECF0; }
.pa-expand-row:last-child { border-bottom: none; }

/* --- dar ekran kırılımları (kardeş sekmelerle aynı eşikler) --- */
@media (max-width: 1200px) {
  .pa-cell-wrap { max-width: 260px; }
  .pa-cell-narrow { max-width: 150px; }
}

@media (max-width: 992px) {
  .pa-toolbar { align-items: stretch; }
  .pa-tabs { overflow-x: auto; }
  .pa-filters { gap: 8px; }
  .pa-card { min-width: 112px; flex: 1 1 112px; padding: 10px 12px; }
  .pa-card-value { font-size: 16px; }
  .pa-cell-wrap { max-width: 200px; min-width: 140px; }
  .pa-cell-narrow { max-width: 130px; }
  .pa-module { padding: 12px; }
}

@media (max-width: 768px) {
  .pa-toolbar { flex-direction: column; align-items: stretch; }
  .pa-date-group { flex-wrap: wrap; }
  .pa-action-btn { width: 100%; }
  .pa-module-header { flex-direction: column; gap: 4px; }
}
</style>
